import { QueryFunctionContext, QueryKey, useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { LimitedSchemaInfiniteQueryOptions } from '@frontend/react-query-helpers';
import { DEFAULT_TASK_LIMIT_V2 } from '../../constants';
import { SchemaTaskCenterV2 } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { queryRequestKeys } from '../keys';

type UseGetTaskListResults<T = IO.GetTaskListV2['output'], E = unknown> = UseInfiniteQueryResult<T, E> & {
  queryKey: QueryKey;
};

/**
 * Get tasks list with listV2.
 */
export const useGetTasksListV2 = <E = unknown, T = IO.GetTaskListV2['output']>(
  req: IO.GetTaskListV2['input'],
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetTaskListV2, E, T>
): UseGetTaskListResults<T, E> => {
  const queryKey = queryRequestKeys.tasksList(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: QueryFunctionContext<QueryKey, IO.GetTaskListV2['input']>) => {
      return SchemaTaskCenterV2.ListTasksV2({
        ...req,
        pagination: {
          ...req.pagination,
          limit: DEFAULT_TASK_LIMIT_V2,
          ...(pageParam?.pagination && { ...pageParam.pagination }),
        },
        includeMetadata: true,
      });
    },
    getNextPageParam: (_, allPages): Partial<IO.GetTaskListV2['input']> | undefined => {
      const lastPageTasks = allPages?.[allPages.length - 1]?.tasks;
      if (!lastPageTasks?.length || lastPageTasks.length < DEFAULT_TASK_LIMIT_V2) return undefined;

      const offset = allPages.length * DEFAULT_TASK_LIMIT_V2;
      return { pagination: { offset } };
    },
    ...defaultOptions,
    retry: false,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};
